import React, { useState, useEffect } from 'react';
import { auth, functions } from '../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import AddressAutocompleteInput from './AddressAutocompleteInput';

/**
 * Edit name / address / postcode for an existing project.
 * Calls updateProjectDetails Cloud Function (geocoding + lat/lng like create).
 */
const EditProjectModal = ({ isOpen, onClose, project, onSaved }) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [country] = useState('UK');
  const [saving, setSaving] = useState(false);

  const setLatLng = (lat, lng) => {
    setLatitude(lat);
    setLongitude(lng);
  };

  useEffect(() => {
    if (isOpen && project) {
      setName(project.name || '');
      setAddress(project.address || '');
      setPostcode(project.postcode || '');
      // Seed with the project's existing coordinates so an unrelated edit (e.g. just the
      // name) doesn't trigger an unnecessary re-geocode - cleared as soon as the user
      // actually touches the address or postcode (see setLatLng usages below).
      setLatitude(project.latitude ?? null);
      setLongitude(project.longitude ?? null);
    }
  }, [isOpen, project]);

  if (!isOpen || !project) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const user = auth.currentUser;
    if (!user) {
      alert('You must be logged in.');
      return;
    }

    setSaving(true);
    try {
      const fn = httpsCallable(functions, 'updateProjectDetails');
      const payload = {
        projectId: project.id,
        name: name.trim(),
        address: address.trim(),
        postcode,
        country,
      };
      if (latitude != null && longitude != null) {
        payload.latitude = latitude;
        payload.longitude = longitude;
      }

      const result = await fn(payload);
      const data = result.data;
      if (!data?.success) {
        throw new Error(data?.message || 'Update failed');
      }

      onSaved?.();
      onClose();
    } catch (err) {
      console.error('EditProjectModal:', err);
      alert(`Failed to update project: ${err.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 999,
      }}
    >
      <div
        style={{
          background: 'var(--cc-card-bg)',
          padding: '30px',
          borderRadius: '10px',
          width: '90%',
          maxWidth: '400px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <h2 style={{ margin: 0, fontSize: '22px', color: 'var(--cc-text-primary)' }}>Edit project details</h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <input
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={{
              padding: '12px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
            }}
          />
          <AddressAutocompleteInput
            address={address}
            setAddress={setAddress}
            setPostcode={setPostcode}
            setLatLng={setLatLng}
            placeholder="Address"
          />
          <input
            type="text"
            placeholder="Postcode"
            value={postcode}
            onChange={(e) => { setPostcode(e.target.value); setLatLng(null, null); }}
            required
            style={{
              padding: '12px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
            }}
          />
          <select
            value={country}
            disabled
            style={{
              padding: '12px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              backgroundColor: 'var(--branding-app-surface)',
              color: 'var(--cc-text-secondary)',
            }}
          >
            <option value="UK">United Kingdom</option>
          </select>
          <input
            type="text"
            value={project.id}
            readOnly
            aria-label="Project code"
            style={{
              padding: '12px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              backgroundColor: 'var(--branding-app-surface)',
              color: 'var(--cc-text-secondary)',
              fontWeight: 'bold',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--cc-text-secondary)',
                fontWeight: 'bold',
                cursor: saving ? 'default' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                background: 'var(--branding-app-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 16px',
                fontWeight: 600,
                cursor: saving ? 'default' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProjectModal;
